/* gs-strive-pdp-can — mobile scroll-pinned animation.

   Section becomes a tall scroll track on mobile. Inside, .__stage is
   `position: sticky` so it pins to the top of the viewport while the
   user scrolls through the section. This script computes a 0→1 scroll
   progress through the section's "active" range and writes CSS
   variables that the CSS uses to drive the animation:

     --can-progress  : 0 → 1 across the FIRST 20% of the section
     --co1-progress  : 0 → 1 across its own window (see CARD_OVERLAP below)
     --co2-progress, --co3-progress, --co4-progress : same, one per card

   NOTE (dynamic card count pass): CARD_COUNT used to be a hardcoded 4,
   with the scroll track's phase math always splitting the post-can
   scroll budget into 4 windows — regardless of how many callout cards
   actually rendered. If a section only has 3 callout blocks (as every
   build so far has), the 4th window still ate its share of scroll
   with nothing on screen to show for it — a long dead stretch at the
   bottom after the last real card settles.

   Now each section counts its own `.gs-strive-pdp-can__callout`
   elements and sizes the phase math off that real count, so the last
   card's window always ends exactly at progress = 1 — no matter
   whether a section has 3, 4, or some other number of callout blocks.

   NOTE (overlap pass): each card's window is LONGER than the stagger
   between window starts, so consecutive windows overlap in
   scroll-progress — card N+1 starts rising before card N's own
   progress has finished. This is independent of the CSS
   --gspdc-callout-travel distance, so cards can still originate fully
   off-screen at the bottom while still overlapping in time.

     CARD_OVERLAP = 0    → no overlap, back-to-back windows
     CARD_OVERLAP = 0.35 → each window overlaps ~35% into the next (default)
     CARD_OVERLAP → 1    → windows nearly coincide (heavy overlap)

   Skipped on desktop (≥992) and when prefers-reduced-motion is set —
   the section then falls back to its static layout.

   Idempotent + Shopify-section-reload safe. */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 991px)';
  var REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

  // --- Card window timing -------------------------------------------------
  var CAN_PHASE = 0.2;          // first 20% of scroll: can slide/rotate only
  var CARD_OVERLAP = 0.7;      // 0 = no overlap (old behavior), 1 = heavy overlap — tune this
  var MAX_CARDS = 4;            // upper bound — CSS only has transform rules for indexes 0–3

  // Computes { length, stagger, starts[] } for a given real card count,
  // solved so the LAST window always ends exactly at progress = 1,
  // regardless of how many cards there are.
  function computeCardTiming(count) {
    var span = 1 - CAN_PHASE; // scroll budget left after the can phase
    var length = span / ((1 - CARD_OVERLAP) * (count - 1) + 1);
    var stagger = length * (1 - CARD_OVERLAP);
    var starts = [];
    for (var i = 0; i < count; i++) {
      starts.push(CAN_PHASE + i * stagger);
    }
    return { length: length, stagger: stagger, starts: starts };
  }
  // --------------------------------------------------------------------

  function clamp01(n) { return n < 0 ? 0 : (n > 1 ? 1 : n); }

  function setVars(section, can, cardProgresses) {
    section.style.setProperty('--can-progress', can.toFixed(4));
    for (var i = 0; i < MAX_CARDS; i++) {
      var v = i < cardProgresses.length ? cardProgresses[i] : 0;
      section.style.setProperty('--co' + (i + 1) + '-progress', v.toFixed(4));
    }
  }

  function arm(section) {
    if (!section || section.dataset.gsPdpCanArmed === '1') return;
    section.dataset.gsPdpCanArmed = '1';
    section.classList.add('gs-strive-pdp-can--js');

    var callouts = section.querySelectorAll('.gs-strive-pdp-can__callout');
    var cardCount = Math.min(MAX_CARDS, callouts.length || MAX_CARDS);
    var timing = computeCardTiming(cardCount);

    var ticking = false;
    var lastProgress = -1;

    /* eslint-disable no-console */
    console.log('[gs-strive-pdp-can] armed', {
      section: section,
      mobile: window.matchMedia(MOBILE_QUERY).matches,
      reducedMotion: window.matchMedia(REDUCED_QUERY).matches,
      offsetHeight: section.offsetHeight,
      vh: window.innerHeight,
      cardCount: cardCount,
      cardLength: timing.length,
      cardStagger: timing.stagger,
      cardStarts: timing.starts
    });
    /* eslint-enable no-console */

    function update() {
      ticking = false;

      // Reduced motion: skip animation, snap to final composition.
      if (window.matchMedia(REDUCED_QUERY).matches) {
        setVars(section, 1, timing.starts.map(function () { return 1; }));
        return;
      }

      // Desktop: clear vars so desktop CSS owns layout.
      if (!window.matchMedia(MOBILE_QUERY).matches) {
        section.style.removeProperty('--can-progress');
        for (var i = 0; i < MAX_CARDS; i++) {
          section.style.removeProperty('--co' + (i + 1) + '-progress');
        }
        return;
      }

      var rect = section.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var range = Math.max(1, section.offsetHeight - vh);

      // The stage is pinned by CSS (position:sticky) — no JS pin work
      // needed here. We only compute progress and write the per-card +
      // can CSS variables. Sticky runs on the compositor thread so the
      // pin itself never lags the scroll.
      var progress = clamp01(-rect.top / range);
      if (progress === lastProgress) return;
      lastProgress = progress;

      // Phase A (0–20%): can slides + rotates.
      // Phase B (20–100%): one window per REAL callout card, sized by
      // computeCardTiming() above so the last one ends at progress = 1.
      var canP = clamp01(progress / CAN_PHASE);
      var cardProgresses = timing.starts.map(function (start) {
        return clamp01((progress - start) / timing.length);
      });
      setVars(section, canP, cardProgresses);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Initial paint.
    update();
  }

  function boot() {
    document.querySelectorAll('.gs-strive-pdp-can').forEach(arm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector
      ? e.target.querySelector('.gs-strive-pdp-can')
      : null;
    if (root) arm(root);
  });
})();